/* Isolated C3oh interactive hero, ported from the Aug 2026 experiment */
(() => {
  const initInteractiveHero = (root) => {
    const triggers = Array.from(root.querySelectorAll('[data-c3oh-hero-trigger]'));
    const panels = Array.from(root.querySelectorAll('[data-c3oh-hero-panel]'));
    const previous = root.querySelector('[data-c3oh-hero-prev]');
    const next = root.querySelector('[data-c3oh-hero-next]');
    const live = root.querySelector('[data-c3oh-hero-live]');
    const pageControls = Array.from(root.querySelectorAll('[data-c3oh-hero-page-control]'));
    const desktopPicker = window.matchMedia('(min-width: 750px)');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let activeIndex = 0;
    let timer = null;
    let inView = true;

    if (!triggers.length || triggers.length !== panels.length) return;

    const stopRotation = () => {
      window.clearTimeout(timer);
      timer = null;
      root.classList.remove('is-rotating');
    };

    const canRotate = () => {
      return !reduceMotion &&
        inView &&
        !document.hidden &&
        !root.matches(':hover') &&
        !root.contains(document.activeElement);
    };

    const syncPickerPage = (index) => {
      const activeTrigger = triggers[index];
      const page = Number(activeTrigger?.dataset.c3ohHeroPickerPage || 0);
      const isDesktop = desktopPicker.matches;

      triggers.forEach((trigger) => {
        const triggerPage = Number(trigger.dataset.c3ohHeroPickerPage || 0);
        const hidden = isDesktop && triggerPage !== page;
        trigger.classList.toggle('is-picker-page-hidden', hidden);
        trigger.tabIndex = hidden ? -1 : 0;

        if (hidden) {
          trigger.setAttribute('aria-hidden', 'true');
        } else {
          trigger.removeAttribute('aria-hidden');
        }
      });

      pageControls.forEach((control) => {
        const selected = Number(control.dataset.c3ohHeroPageControl || 0) === page;
        control.classList.toggle('is-active', selected);
        control.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
    };

    const scheduleRotation = () => {
      stopRotation();
      if (!canRotate()) return;
      void root.offsetWidth;
      root.classList.add('is-rotating');
      timer = window.setTimeout(() => {
        setActive(activeIndex + 1, false);
        scheduleRotation();
      }, 5000);
    };

    const setActive = (nextIndex, announce = true) => {
      activeIndex = (nextIndex + panels.length) % panels.length;
      syncPickerPage(activeIndex);

      triggers.forEach((trigger, index) => {
        const selected = index === activeIndex;
        trigger.classList.toggle('is-active', selected);
        trigger.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });

      panels.forEach((panel, index) => {
        const selected = index === activeIndex;
        panel.classList.toggle('is-active', selected);
        panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
        panel.inert = !selected;
      });

      const activeTrigger = triggers[activeIndex];
      const optionStrip = activeTrigger.parentElement;
      if (optionStrip && optionStrip.scrollWidth > optionStrip.clientWidth) {
        const targetLeft = activeTrigger.offsetLeft - ((optionStrip.clientWidth - activeTrigger.offsetWidth) / 2);
        optionStrip.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }

      if (announce && live) {
        const panel = panels[activeIndex];
        live.textContent = 'Showing ' + panel.dataset.productTitle + ' for ' + panel.dataset.direction + '.';
      }
    };

    triggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        setActive(index);
        scheduleRotation();
      });
    });

    pageControls.forEach((control) => {
      control.addEventListener('click', () => {
        const page = Number(control.dataset.c3ohHeroPageControl || 0);
        const firstIndex = triggers.findIndex(
          (trigger) => Number(trigger.dataset.c3ohHeroPickerPage || 0) === page
        );

        if (firstIndex >= 0) {
          setActive(firstIndex);
          scheduleRotation();
        }
      });
    });

    previous?.addEventListener('click', () => {
      setActive(activeIndex - 1);
      scheduleRotation();
    });

    next?.addEventListener('click', () => {
      setActive(activeIndex + 1);
      scheduleRotation();
    });

    root.addEventListener('pointerenter', stopRotation);
    root.addEventListener('pointerleave', scheduleRotation);
    root.addEventListener('focusin', stopRotation);
    root.addEventListener('focusout', () => window.setTimeout(scheduleRotation, 0));

    document.addEventListener('visibilitychange', scheduleRotation);

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          scheduleRotation();
        },
        { threshold: 0.22 }
      );
      observer.observe(root);
    }

    const handlePickerViewportChange = () => syncPickerPage(activeIndex);
    if (desktopPicker.addEventListener) {
      desktopPicker.addEventListener('change', handlePickerViewportChange);
    } else {
      desktopPicker.addListener(handlePickerViewportChange);
    }

    setActive(0, false);
    scheduleRotation();
  };

  const initialise = () => {
    document.querySelectorAll('[data-c3oh-hero]').forEach(initInteractiveHero);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
